import { DashInvite, SortDirectionEnum } from "@/__generated__/types";
import { DataTable } from "@/components/DataTable";
import { Column, ColumnDef, Row } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { formatDate } from "@/lib/formatDate";
import { useLocale } from "@/hooks/useLocale";
import {
  DashInviteOrderingInput,
  DashInviteRoleEnum,
  DashInviteSortableFieldEnum,
} from "@/__generated__/graphql";
import { formatPhoneNumber } from "@/util/phoneUtils";
import { NameAndAvatar } from "@/components/StudentListWrapper/Grid/NameAndAvatar";
import { useOrg } from "@/hooks/useOrg";
import { useQuery } from "@apollo/client";
import { CURRENT_USER_PERMISSIONS } from "@/api/queries/currentUserPermissions";
import { InivteTableActions } from "../InviteTableActions";

const DATE_FORMAT = "PPP";

const canSort = (field: string): boolean => {
  return Object.values(DashInviteSortableFieldEnum).includes(
    field as DashInviteSortableFieldEnum,
  );
};

const MAP_SORTING_FIELD_TO_COLUMN_ID: Record<
  string,
  DashInviteSortableFieldEnum
> = {
  name: DashInviteSortableFieldEnum.Name,
  role: DashInviteSortableFieldEnum.Role,
  invitedAt: DashInviteSortableFieldEnum.InvitedAt,
};

export const ROLE_MAP = {
  [DashInviteRoleEnum.Counselor]: "teamManagement.table.counselor",
  [DashInviteRoleEnum.Influencer]: "teamManagement.table.influencer",
};

export function useInviteTableConfig() {
  const { t } = useTranslation();
  const locale = useLocale();
  const { org } = useOrg();
  const { data } = useQuery(CURRENT_USER_PERMISSIONS, {
    variables: { organizationId: org?.id ?? "" },
    skip: !org?.id,
  });
  const [ordering, setOrdering] = useState<DashInviteOrderingInput[]>([
    {
      field: DashInviteSortableFieldEnum.Name,
      direction: SortDirectionEnum.Asc,
    },
  ]);

  const handleSortingChange = ({
    orderingField,
    orderingDirection,
  }: {
    orderingField: string;
    orderingDirection: SortDirectionEnum;
  }) => {
    setOrdering([
      {
        field: MAP_SORTING_FIELD_TO_COLUMN_ID[orderingField],
        direction: orderingDirection,
      },
    ]);
  };

  const columnsConfig: ColumnDef<Partial<DashInvite> | null>[] = [
    {
      id: "name",
      accessorFn: (row: Partial<DashInvite> | null) =>
        `${row?.firstName} ${row?.lastName}`,
      enableSorting: canSort("name"),
      header: ({ column }: { column: Column<Partial<DashInvite> | null> }) => (
        <DataTable.THeaderCellSorting
          column={column}
          onSortingChange={handleSortingChange}
        >
          {t("teamManagement.table.name")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<Partial<DashInvite> | null> }) => {
        return (
          <DataTable.TBodyCell>
            <NameAndAvatar
              firstName={row.original?.firstName}
              lastName={row.original?.lastName}
              avatarSize={"size-6"}
            />
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "role",
      accessorFn: (row: Partial<DashInvite> | null) =>
        t(ROLE_MAP[row?.role as DashInviteRoleEnum]),
      enableSorting: canSort("role"),
      enableColumnFilter: true,
      meta: {
        label: t("teamManagement.table.role"),
      },
      header: ({ column }: { column: Column<Partial<DashInvite> | null> }) => (
        <DataTable.THeaderCellSorting
          column={column}
          onSortingChange={handleSortingChange}
        >
          {t("teamManagement.table.role")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<Partial<DashInvite> | null> }) => {
        return (
          <DataTable.TBodyCell>
            {t(ROLE_MAP[row.original?.role as DashInviteRoleEnum])}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "phone",
      accessorFn: (row: Partial<DashInvite> | null) => row?.phoneNumber ?? "",
      enableSorting: canSort("phone"),
      meta: {
        label: t("teamManagement.table.phone"),
      },
      header: () => (
        <DataTable.THeaderCell>
          {t("teamManagement.table.phone")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<Partial<DashInvite> | null> }) => {
        return (
          <DataTable.TBodyCell>
            {formatPhoneNumber(row.original?.phoneNumber)}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "email",
      accessorFn: (row: Partial<DashInvite> | null) => row?.email ?? "",
      enableSorting: canSort("email"),
      meta: {
        label: t("teamManagement.table.email"),
      },
      header: () => (
        <DataTable.THeaderCell>
          {t("teamManagement.table.email")}
        </DataTable.THeaderCell>
      ),
      cell: ({ row }: { row: Row<Partial<DashInvite> | null> }) => {
        return <DataTable.TBodyCell>{row.original?.email}</DataTable.TBodyCell>;
      },
    },
    {
      id: "invitedAt",
      accessorFn: (row: Partial<DashInvite> | null) => {
        const { invitedAt } = row || {};
        return invitedAt
          ? formatDate(invitedAt, DATE_FORMAT, {
              locale,
            })
          : "---";
      },
      enableSorting: canSort("invitedAt"),
      meta: {
        label: t("teamManagement.table.invitedDate"),
      },
      header: ({ column }: { column: Column<Partial<DashInvite> | null> }) => (
        <DataTable.THeaderCellSorting
          column={column}
          onSortingChange={handleSortingChange}
        >
          {t("teamManagement.table.invitedDate")}
        </DataTable.THeaderCellSorting>
      ),
      cell: ({ row }: { row: Row<Partial<DashInvite> | null> }) => {
        return (
          <DataTable.TBodyCell>
            {row.original?.invitedAt
              ? formatDate(row.original?.invitedAt, DATE_FORMAT, {
                  locale,
                })
              : "---"}
          </DataTable.TBodyCell>
        );
      },
    },
    {
      id: "ACTIONS",
      header: "",
      enableSorting: false,
      enableColumnFilter: false,
      cell: ({ row }: { row: Row<Partial<DashInvite> | null> }) => (
        <div className="h-9">
          {data?.me?.isSuperCounselor && <InivteTableActions row={row} />}
        </div>
      ),
    },
  ];

  return { columns: columnsConfig, ordering };
}
